import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { CheckCircleOutlined, RightOutlined } from '@ant-design/icons';
import {
  openModal as openBalloon,
  closeModal as closeBalloon,
} from 'redux/Reducers/PopBallon/VertemGiftSlice';
import useActiveCampaign from 'Hooks/useVertemGift';

// COMPONENTS
import OrderDetails from 'components/Orders/OrderDetails/OrderDetails';

// DS
import {
  Col,
  VertButton,
  VertContainer,
  VertIcon,
  VertModal,
  VertTypography,
  VertBreadcrumb,
  VertSkeleton,
} from '@vertem-core/vertice-ds';

import PipeProvider from '../../Services/Providers/PipeProvider';
import usePipeContext from 'Services/Providers/PipeProvider/usePipeContext';

import generateUUID from 'Utils/generateUUID';
import ynToBool from 'Utils/ynToBool';
import { pointsMask } from 'Utils/commons/points';
import { addDimensionsConfirmationGTM } from 'Shared/dataLayer/dataLayer';
import { FETCH_RESET_PURCHASE } from '../../redux/Reducers/Checkout/CheckoutReducer';
import BalloonModal from '../../components/PopBallon/ShowcasePopBallon/BallonModal';
import { getAvailableBallons } from '../../redux/Reducers/PopBallon/VertemGiftApi';
import useSupplier from 'Hooks/useSupplier';

const OrderDetailsPage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { callApi } = usePipeContext();
  const data = useSelector(({ OrderDetails: data }) => data);

  const CreatePurchase = useSelector(({ CreatePurchase: data }) => data);
  const Balance = useSelector(({ Balance: data }) => data);
  const Catalog = useSelector(({ Catalog: data }) => data);
  const [createBalloon, setCreateBalloon] = useState(false);
  const { isCampaignActive } = useActiveCampaign();

  const [isOpenSuccessModal, setIsOpenSuccessModal] = useState(false);
  const [customAliasUUID, setCustomAliasUUID] = useState(generateUUID());

  const isLoading = data?.isLoading;
  const queryParams = new URLSearchParams(location.search);
  const orderId = queryParams.get('orderId');

  const { isModalOpen, availableBallons } = useSelector(({ VertemGift }) => VertemGift);

  const isFromCheckout = ynToBool(JSON.parse(queryParams.get('fromCheckout')));
  const isFromLikePlus = ynToBool(JSON.parse(queryParams.get('fromLikePlus')));
  const order = data.orders ? data.orders[0] : [];

  const {
    currencyConfiguration: { decimalPlaces, pointsName },
  } = Catalog;
  const remainingBalance =
    Balance?.pointsValue && pointsMask(Balance.pointsValue, decimalPlaces, pointsName);
  const breadcrumbItems = [
    {
      title: 'Início',
      path: '/',
    },
    {
      title: 'Meus pedidos',
      path: `/my-orders`,
    },
    {
      title: 'Detalhes do resgate',
      path: isFromCheckout
        ? `/order-details?orderId=${orderId}&fromCheckout=${isFromCheckout}`
        : `/order-details?orderId=${orderId}&fromLikePlus=${isFromLikePlus}`,
    },
  ];

  const handleModalClose = () => {
    dispatch(closeBalloon());
  };

  useEffect(() => {
    if (!orderId) {
      navigate('/my-orders');
    }

    if (isFromCheckout || isFromLikePlus) {
      setIsOpenSuccessModal(true);
    }
  }, []);

  useEffect(() => {
    if (order?.createDate && (isFromCheckout || isFromLikePlus)) {
      addDimensionsConfirmationGTM(order);
    }
  }, [order]);

  useEffect(() => {
    if (isFromCheckout || isFromLikePlus) {
      const loadBalanceAlias = generateUUID();
      callApi({
        symbol: 'LOAD_BALANCE',
        alias: loadBalanceAlias,
      });
    }
  }, [isFromCheckout, isFromLikePlus]);

  useEffect(() => {
    if (CreatePurchase?.finishRequests) {
      dispatch({
        type: FETCH_RESET_PURCHASE,
        payload: {},
      });
    }
  }, [CreatePurchase]);

  const [isLoadingBalloon, setIsLoadingBalloon] = useState(true);

  useEffect(() => {
    if (ynToBool(process.env.REACT_APP_HAS_VERTEM_GIFT) && isCampaignActive) {
      const fetchData = async () => {
        if (queryParams.get('fromCheckout') === 'true') {
          dispatch(getAvailableBallons());
          setIsLoadingBalloon(false);
        }
      };

      fetchData();
    }
  }, [isCampaignActive]);

  useEffect(() => {
    if (isCampaignActive && ynToBool(process.env.REACT_APP_HAS_VERTEM_GIFT)) {
      const handleBalloonStatus = async () => {
        if (!isLoadingBalloon && availableBallons.length > 0) {
          dispatch(openBalloon());
        }
      };

      handleBalloonStatus();
    }
  }, [isLoadingBalloon, availableBallons, isCampaignActive]);

  /* Renders */
  if (isLoading) {
    return (
      <VertContainer data-testid='order-details-page' className='order-details-wrapper'>
        <VertSkeleton paragraph={{ rows: 30 }} active />
      </VertContainer>
    );
  }
  return (
    <>
      <PipeProvider
        apiSymbols={[
          {
            symbol: 'FETCH_ORDER_DETAILS',
            alias: customAliasUUID,
            payload: { orderId: orderId, offset: 0, limit: 1 },
          },
        ]}
      >
        <VertContainer data-testid='order-details-page' className='order-details-wrapper'>
          <VertBreadcrumb
            hasBackButton={true}
            separator={<VertIcon component={RightOutlined} />}
            items={breadcrumbItems}
          />

          <OrderDetails order={order} />

          {isOpenSuccessModal && (
            <VertModal
              open={isOpenSuccessModal}
              rootClassName='order-placed-success-modal'
              template='marketplace'
              spacing='regular'
            >
              <Col className='order-placed-success-content'>
                <VertIcon component={CheckCircleOutlined} />
                <VertTypography.Paragraph className='title'>
                  Resgate realizado com sucesso!
                </VertTypography.Paragraph>
                <VertTypography.Paragraph className='message'>
                  Enviamos o status do pedido para seu e-mail de cadastro.
                </VertTypography.Paragraph>
                <VertTypography.Paragraph className='subtitle'>
                  Saldo restante: {remainingBalance.value} {remainingBalance.label}
                </VertTypography.Paragraph>
                <VertButton
                  type='highlight'
                  onClick={() => {
                    setIsOpenSuccessModal(false);
                    setCreateBalloon(true);
                  }}
                >
                  Entendi
                </VertButton>
              </Col>
            </VertModal>
          )}
        </VertContainer>
      </PipeProvider>
      {isModalOpen && createBalloon && (
        <BalloonModal balloon={availableBallons[0]} onClose={handleModalClose} />
      )}
    </>
  );
};

export default OrderDetailsPage;
